
import { computed, defineComponent, PropType, ref } from 'vue';
import { Data } from './ts/model/Data';
import { Link } from './ts/model/Link';
import Pt from './ts/model/Pt';
import { Skill } from './ts/model/Skill';
import { settings } from './ts/settings';
import * as d3 from 'd3';
import { D3ZoomEvent, select } from 'd3';
import SvgIcon from './components/SvgIcon.vue';
import SkillDetails from './components/SkillDetails.vue';

export default defineComponent({
  components: { SvgIcon, SkillDetails },
  setup(props) {
    const graphSvg = ref<SVGSVGElement>();

    const mainGroup = ref<SVGGElement>();
    const drawingGroup = ref<SVGGElement>();

    function setZoom(transform: d3.ZoomTransform): void {
      console.log(`Transform: ${transform.toString()}`);

      if (isNaN(transform.x) || isNaN(transform.y) || isNaN(transform.k)) {
        return;
      }

      mainGroup.value?.setAttribute('transform', transform.toString());

      mainGroup.value?.classList.toggle('detailed', transform.k > 4);
    }

    const width = computed(() => graphSvg.value?.clientWidth ?? 0);

    const height = computed(() => graphSvg.value?.clientHeight ?? 0);

    const drawingRadius = computed(() =>
      props.data.tree.scale(props.data.tree.skillRange[1])
    );

    const maxCircleRadius = computed(() => {
      let radius = drawingRadius.value * Math.SQRT2;

      if (width.value && height.value) {
        radius *= Math.max(
          width.value / height.value,
          height.value / width.value
        );
      }

      return radius;
    });

    return {
      graphSvg,
      mainGroup,
      drawingGroup,
      scale: props.data.tree.scale,
      settings,
      zoom: d3
        .zoom<SVGSVGElement, unknown>()
        // .scaleExtent([0.8, 6])
        // .translateExtent([
        //   [-drawingRadius.value, -drawingRadius.value],
        //   [drawingRadius.value * 2, drawingRadius.value * 2],
        // ])
        .on('zoom', (event: D3ZoomEvent<SVGSVGElement, unknown>) => {
          setZoom(event.transform);
        }),
      width,
      height,
      drawingRadius,
      maxCircleRadius,
    };
  },
  props: {
    data: {
      type: Object as PropType<Data>,
      required: true,
    },
  },
  data() {
    return {
      selection: undefined as Skill | undefined,
    };
  },
  computed: {
    gridLevels() {
      return Array(Math.floor(this.scale.invert(this.maxCircleRadius)))
        .fill(0)
        .map((_, i) => i)
        .filter(
          (_, i) =>
            !(i % 12 && i > settings.rings.lastQuarterly)
            && !(i % 12 && i % 3 && i > settings.rings.lastMonthly)
        );
    },
    maxYear() {
      return Math.floor(this.data.tree.skillRange[1] / 12);
    },
    skillLengths() {
      return new Set(
        Object.values(this.data.tree.skills).map((s) => s.roundedLength)
      );
    },
    innerHeight() {
      return settings.layout.skillWidth - 2 * settings.layout.skillMargin;
    },
  },
  async mounted() {
    if (this.graphSvg) {
      select(this.graphSvg).call(this.zoom);

      this.zoomFit(false);
    }
  },
  methods: {
    tooltipTitle(skill: Skill) {
      return `${skill.name}: ${skill.rangeString()}`;
    },
    select(skill: Skill | undefined, event: Event): void {
      if (skill?.id === this.selection?.id) {
        this.selection = undefined;
      } else {
        this.selection = skill;
      }

      this.graphSvg
        ?.querySelectorAll('.skill')
        .forEach((s) =>
          s.classList.toggle('selected', s.id === this.selection?.id)
        );

      event.stopPropagation();
    },
    skillOutlineId(length: number): string {
      return `skillOutline-${length.toString().replace('.', '-')}`;
    },
    innerTransform(skill: Skill): string {
      if (skill.reversed) {
        return `rotate(180) translate(${-skill.barRanges.total.end})`;
      } else {
        return `translate(${skill.barRanges.total.start})`;
      }
    },
    iconAttributes(skill: Skill) {
      return {
        y: -this.innerHeight / 2,
        x: skill.reversed
          ? skill.barRanges.total.length
            - settings.layout.skillMargin
            - this.innerHeight
          : settings.layout.skillMargin,
        height: this.innerHeight,
        width: this.innerHeight,
      };
    },
    smoothLinkPath(link: Link): string {
      const start = link.source;
      const end = link.target;

      const pt1 = Pt.fromPolar(
        start.barRanges.total.end,
        (start.angle * Math.PI) / 180
      );
      const pt2 = Pt.fromPolar(
        start.barRanges.total.end + this.settings.layout.linkTangent,
        (start.angle * Math.PI) / 180
      );

      const pt3 = Pt.fromPolar(
        end.barRanges.total.start - this.settings.layout.linkTangent,
        (end.angle * Math.PI) / 180
      );
      const pt4 = Pt.fromPolar(
        end.barRanges.total.start,
        (end.angle * Math.PI) / 180
      );

      return `M ${pt1} C ${pt2} ${pt3} ${pt4}`;
    },
    zoomFit(animate = true, paddingPercent = 0.85): void {
      const bounds = this.drawingGroup?.getBBox();
      const fullWidth = this.graphSvg?.clientWidth,
        fullHeight = this.graphSvg?.clientHeight;

      if (!bounds || !fullWidth || !fullHeight) {
        throw new Error('Nodes not found.');
      }

      const width = bounds.width,
        height = bounds.height;

      const midX = bounds.x + width / 2,
        midY = bounds.y + height / 2;

      if (width == 0 || height == 0) return; // nothing to fit

      const scale =
        paddingPercent / Math.max(width / fullWidth, height / fullHeight);
      const translate: [number, number] = [-midX, -midY];

      console.debug('zoomFit', translate, scale);

      if (this.graphSvg) {
        if (animate) {
          select(this.graphSvg)
            .transition()
            .duration(750)
            .call(
              this.zoom.transform,
              d3.zoomIdentity
                .translate(fullWidth / 2, fullHeight / 2)
                .scale(scale)
                .translate(...translate)
            );
        } else {
          select(this.graphSvg).call(
            this.zoom.transform,
            d3.zoomIdentity
              .translate(fullWidth / 2, fullHeight / 2)
              .scale(scale)
              .translate(...translate)
          );
        }
      }
    },
  },
});
